/**
 * Pure (DOM-free) rules for the Stage-3 Generation surface (E03-F04-T07) and the
 * document editor (E03-F05-T04). Kept out of the React components so the decisions
 * are unit-testable in isolation and shared between the two views:
 *
 *   - `artifactStatusOf` — map a project's artifact map → the per-card status
 *     (not_generated / draft / generated / edited / approved / sent);
 *   - `badgeToneClass` — the Tailwind classes for a status badge tone;
 *   - `cardActionsFor` — which actions a card exposes for a given status/type;
 *   - `countGenerated` / `countApproved` — the header counters;
 *   - `renderMarkdownInline` — the legacy `_renderMarkdownInline` (`**` → bold,
 *     `*`/`_` → italic, `__` → underline) returning safe inline segments (no HTML
 *     injection — the component renders the segments, never `dangerouslySetInnerHTML`);
 *   - `applyNotifTemplate` — `{{var}}` substitution for the notification templates.
 *
 * All values come from the shared `@eer/shared` doc-generation contract
 * (GENERATION_ORDER / ARTIFACT_STATUS_BADGES / COMMUNICATION_ITEMS / …).
 */
import {
  ARTIFACT_STATUS_BADGES,
  GENERATION_ORDER,
  INTERVIEW_QUESTION_FIELDS,
  SOURCING_SHEET_FIELDS,
  humanFieldLabel,
  isCommunicationItem,
  isEmptyDocValue,
  isSendableDoc,
  type DocFieldSpec,
  type PACKAGE_DOC_TYPES,
  resolveDocFields,
  SOURCING_SHEET_TYPE,
  type ArtifactStatus,
} from '@eer/shared';

/** An artifact as the SPA holds it (subset of project_artifacts). */
export interface ArtifactLike {
  readonly type: string;
  readonly status?: string | null;
  readonly content?: unknown;
}

/** Map of artifact type → artifact (the project's current artifacts). */
export type ArtifactMap = Readonly<Record<string, ArtifactLike | undefined>>;

/** All statuses that count as "the doc exists" (anything past not_generated). */
const EXISTING: readonly ArtifactStatus[] = ['draft', 'generated', 'edited', 'approved', 'sent'];

/** Coerce an unknown artifact status string into a known `ArtifactStatus`. */
export function normalizeStatus(raw: string | null | undefined): ArtifactStatus {
  if (raw && raw in ARTIFACT_STATUS_BADGES) return raw as ArtifactStatus;
  return 'not_generated';
}

/**
 * The card status for a doc type: `not_generated` when absent, otherwise the
 * artifact's own status (normalised). A missing/blank status on a present
 * artifact is treated as `generated` (it exists but carries no lifecycle yet).
 */
export function artifactStatusOf(map: ArtifactMap, type: string): ArtifactStatus {
  const a = map[type];
  if (!a) return 'not_generated';
  const s = normalizeStatus(a.status);
  return s === 'not_generated' ? 'generated' : s;
}

/** Badge tone → Tailwind classes (approved=highlight gold, sent=success green). */
export function badgeToneClass(tone: 'neutral' | 'info' | 'success' | 'highlight'): string {
  switch (tone) {
    case 'highlight':
      return 'bg-[var(--color-accent)]/20 text-[var(--color-accent-hover)] border border-[var(--color-accent)]';
    case 'success':
      return 'bg-[var(--color-success)]/15 text-[var(--color-success)] border border-[var(--color-success)]';
    case 'info':
      return 'bg-[var(--color-info)]/10 text-[var(--color-info)]';
    case 'neutral':
    default:
      return 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]';
  }
}

/** The badge descriptor (label + tone classes) for a status. */
export function badgeFor(status: ArtifactStatus): { label: string; toneClass: string } {
  const b = ARTIFACT_STATUS_BADGES[status];
  return { label: b.label, toneClass: badgeToneClass(b.tone) };
}

/**
 * True when this item is a communication item (Send vs Download).
 *
 * Re-exported from `@eer/shared` (E05-F06-T09): the predicate moved next to
 * `isSendableDoc` so the "sendable but still downloadable" split has ONE home; the
 * name stays exported here because the editor's fast-track gate and the card model
 * already import it from this module.
 */
export { isCommunicationItem };

/** The action set a generation card exposes for a given type+status. */
export interface CardActions {
  /** First-generation button shown when the doc does not exist yet. */
  readonly canGenerate: boolean;
  /** View/Edit available once the doc exists. */
  readonly canView: boolean;
  /** Regenerate available once the doc exists. */
  readonly canRegenerate: boolean;
  /** Approve available once it exists and is not yet approved/sent. */
  readonly canApprove: boolean;
  /** Download — approved non-comm doc. */
  readonly canDownload: boolean;
  /** Send — approved comm item. */
  readonly canSend: boolean;
}

/** Decide the available actions for a card (T07 acceptance #5). */
export function cardActionsFor(type: string, status: ArtifactStatus): CardActions {
  const exists = (EXISTING as readonly string[]).includes(status);
  const approved = status === 'approved' || status === 'sent';
  const comm = isCommunicationItem(type);
  return {
    canGenerate: !exists,
    canView: exists,
    canRegenerate: exists,
    canApprove: exists && !approved,
    canDownload: status === 'approved' && !comm,
    // E05-F06-T09 — Sourcing Sheet is sendable too, on the Kickoff Sheet's terms:
    // its "Send to Sourcing Team" button appears as soon as the document exists,
    // and the client asked for «такая же кнопка, как у Kickoff Sheet». The
    // client-FACING follow-up email keeps its approval gate — an unapproved message
    // must never reach the client, while an internal sheet going to our own sourcers
    // is exactly what recruiters send before the package is formally approved.
    canSend: isSendableDoc(type) && (comm ? status === 'approved' : exists),
  };
}

/** Count of generated (existing) docs across the rendered generation cards. */
export function countGenerated(map: ArtifactMap): number {
  return GENERATION_CARDS.reduce(
    (n, t) => n + (artifactStatusOf(map, t) === 'not_generated' ? 0 : 1),
    0,
  );
}

/** Count of approved (or sent) docs across the rendered generation cards. */
export function countApproved(map: ArtifactMap): number {
  return GENERATION_CARDS.reduce((n, t) => {
    const s = artifactStatusOf(map, t);
    return n + (s === 'approved' || s === 'sent' ? 1 : 0);
  }, 0);
}

/**
 * E03-F07-T07 — the «Kickoff done mail» card's Sent label from the backend
 * `project.internal_notification_sent_at` (latest `internal_notification_sent`
 * event). `null` when never sent or the timestamp is blank/unparseable, so the
 * card shows no badge rather than "Sent Invalid Date".
 */
export function notificationSentLabel(sentAt: string | null | undefined): string | null {
  const raw = (sentAt ?? '').trim();
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const when = d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Sent ${when}`;
}

/**
 * Every card type rendered in the Generation grid, in order. The GENERATION_ORDER
 * items (kickoff_sheet / client_followup_email / jd_doc / inmails /
 * interview_questions) PLUS `sourcing_sheet` — a real package deliverable that the
 * backend confirm-gate REQUIRES (`PACKAGE_DOC_TYPES`) but which had no dedicated
 * GENERATION_ORDER slot, so "Confirm & Move to Final" was structurally
 * unsatisfiable from the UI (PK-QA-31). `as const` so the element union below is
 * exact.
 */
export const GENERATION_CARDS = [...GENERATION_ORDER, SOURCING_SHEET_TYPE] as const;

/** A card rendered in the Generation grid (the GENERATION_ORDER items + sourcing_sheet). */
export type GenerationCardType = (typeof GENERATION_CARDS)[number];

/**
 * PK-QA-31 static invariant — `PACKAGE_DOC_TYPES ⊆ approvable-card-set`. The
 * approvable surface (the rendered cards) MUST cover every doc type the backend
 * confirm-gate requires, or Confirm is structurally impossible. `GenerationCardType`
 * is derived from the RUNTIME `GENERATION_CARDS` array, so if a future edit drops a
 * required type from the grid (or adds one to `PACKAGE_DOC_TYPES` without a card),
 * `(typeof PACKAGE_DOC_TYPES)[number]` stops being assignable to `GenerationCardType`
 * and THIS LINE fails to compile (`false` is not assignable to `true`). This makes
 * the coverage guarantee mechanically enforced, not merely true today.
 */
type PackageTypesCoveredByCards = (typeof PACKAGE_DOC_TYPES)[number] extends GenerationCardType
  ? true
  : false;
const _packageTypesCoveredByCards: PackageTypesCoveredByCards = true;
void _packageTypesCoveredByCards;

// ---------------------------------------------------------------------------
// Inline markdown (legacy _renderMarkdownInline) — returns safe segments
// ---------------------------------------------------------------------------

// PK-QA-35 — `renderMarkdownInline` / `InlineSegment` (and the internal
// `INLINE_RULES` table) were relocated verbatim to `@eer/shared` (new module
// `markdown.ts`) so `@eer/api` can render outbound-email markdown with the SAME
// single grammar. Re-exported here so existing call sites keep importing them
// from `./docGenerationModel.js` unchanged (DocumentView, docViewModel, tests).
export { renderMarkdownInline } from '@eer/shared';
export type { InlineSegment } from '@eer/shared';

// ---------------------------------------------------------------------------
// Notification template substitution ({{var}} → value) — _applyNotifTemplate
// ---------------------------------------------------------------------------

/** Substitute `{{key}}` tokens in a template body with the supplied vars. */
export function applyNotifTemplate(
  template: string,
  vars: Readonly<Record<string, string>>,
): string {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) => vars[key] ?? '');
}

/**
 * Split a raw notification template into its `Subject:` first line and the body
 * remainder (legacy templates put `Subject: …` on line 1). When no subject line
 * is present the whole string is the body.
 */
export function splitTemplate(raw: string): { subject: string; body: string } {
  const lines = raw.split('\n');
  const first = lines[0] ?? '';
  const m = first.match(/^\s*subject:\s*(.*)$/i);
  if (m) {
    return { subject: m[1]!.trim(), body: lines.slice(1).join('\n').replace(/^\n+/, '') };
  }
  return { subject: '', body: raw };
}

// ---------------------------------------------------------------------------
// Per-type editor field config (renderDocEditForm) — E03-F05-T04
// ---------------------------------------------------------------------------

/** A single field in the per-type document edit form. */
export interface DocEditField {
  /**
   * The content path this field reads/writes — a plain key, or a dotted path
   * into a nested object (`sourcing_sheet.position_title`) for the Sourcing
   * Sheet a Kickoff Sheet carries inside itself. A synthetic id for `group`.
   */
  readonly key: string;
  /** Human label. */
  readonly label: string;
  /**
   * `text`     — single-line input, NO formatting toolbar. Legacy forms only
   *              (an email Subject); never produced from document content, see
   *              `kindForValue`;
   * `richline` — short value in the rich-text editor: same toolbar as the rest,
   *              compact box;
   * `markdown` — long prose in the rich-text editor;
   * `list`     — one item per line (array<string>), rich-text editor;
   * `variants` — an array of structured `{tone,subject,body}` objects, edited as
   *              one Subject input + Body textarea PER variant (InMails). Distinct
   *              from `list` (which flattens to a `string[]`) — a `list` round-trip
   *              would irreversibly discard tone/subject and corrupt the artifact
   *              (E03-F05-T12);
   * `comments` — JD *_comments: read-only, grey, not edited (saved as-is);
   * `group`    — a non-field section header (legacy `<h4>` group label; renders no
   *              input and writes nothing).
   */
  readonly kind: 'text' | 'richline' | 'markdown' | 'list' | 'variants' | 'comments' | 'group';
}

/**
 * The editor form schema per document type (renderDocEditForm parity). JD has
 * the richest shape: markdown prose sections each paired with a read-only
 * `_comments` block. The other types are lists / markdown bodies.
 */
export const DOC_EDIT_FORMS: Record<string, readonly DocEditField[]> = {
  jd_doc: [
    { key: 'about_company', label: 'About Company', kind: 'markdown' },
    { key: 'about_company_comments', label: 'About Company — comments', kind: 'comments' },
    { key: 'about_role', label: 'About Role', kind: 'markdown' },
    { key: 'about_role_comments', label: 'About Role — comments', kind: 'comments' },
    { key: 'responsibilities', label: 'Responsibilities', kind: 'list' },
    { key: 'responsibilities_comments', label: 'Responsibilities — comments', kind: 'comments' },
    { key: 'requirements', label: 'Requirements', kind: 'list' },
    { key: 'requirements_comments', label: 'Requirements — comments', kind: 'comments' },
    { key: 'company_offers', label: 'What Company Offers', kind: 'markdown' },
    { key: 'company_offers_comments', label: 'Company Offers — comments', kind: 'comments' },
  ],
  interview_questions: [
    { key: 'technical', label: 'Technical', kind: 'list' },
    { key: 'behavioral', label: 'Behavioral', kind: 'list' },
    { key: 'culture_fit', label: 'Culture fit', kind: 'list' },
    { key: 'client_specific', label: 'Client specific', kind: 'list' },
  ],
  inmails: [{ key: 'variants', label: 'InMail variants', kind: 'variants' }],
  // Full structured Sourcing Sheet (legacy renderDocEditForm sourcing_sheet ~L868):
  // ~24 fields across grouped sections. `text`/`list` kinds map to string / array<string>.
  sourcing_sheet: [
    { key: 'position_title', label: 'Position', kind: 'text' },
    { key: 'alternate_titles', label: 'Alternate titles', kind: 'list' },
    { key: 'non_relevant_titles', label: 'Non-relevant titles', kind: 'list' },
    { key: '_grp_skillsets', label: 'Skillsets', kind: 'group' },
    { key: 'skillsets_priority_1', label: 'Priority 1', kind: 'list' },
    { key: 'skillsets_priority_2', label: 'Priority 2', kind: 'list' },
    { key: 'skillsets_priority_3', label: 'Priority 3', kind: 'list' },
    { key: '_grp_keywords', label: 'Keywords', kind: 'group' },
    { key: 'keywords_priority_1', label: 'Priority 1', kind: 'list' },
    { key: 'keywords_priority_2', label: 'Priority 2', kind: 'list' },
    { key: 'keywords_priority_3', label: 'Priority 3', kind: 'list' },
    { key: 'keywords_exclude', label: 'Exclude', kind: 'list' },
    { key: '_grp_experience', label: 'Experience', kind: 'group' },
    { key: 'experience_ideal', label: 'Ideal', kind: 'text' },
    { key: 'experience_possible', label: 'Possible', kind: 'text' },
    { key: 'experience_out_of_range', label: 'Out of Range', kind: 'text' },
    { key: '_grp_education', label: 'Education', kind: 'group' },
    { key: 'education_must', label: 'Must', kind: 'text' },
    { key: 'education_nice', label: 'Nice to Have', kind: 'text' },
    { key: '_grp_location', label: 'Location', kind: 'group' },
    { key: 'location_priority_1', label: 'Priority 1', kind: 'text' },
    { key: 'location_priority_2', label: 'Priority 2', kind: 'text' },
    { key: 'location_priority_3', label: 'Priority 3', kind: 'text' },
    { key: '_grp_companies', label: 'Companies & Industries', kind: 'group' },
    { key: 'competition_companies', label: 'Relevant companies', kind: 'list' },
    { key: 'dns_companies', label: 'DNS companies', kind: 'list' },
    { key: 'industry_ideal', label: 'Industry (Ideal)', kind: 'text' },
    { key: 'industry_possible', label: 'Industry (Also Possible)', kind: 'text' },
    { key: 'industry_non_relevant', label: 'Industry (Non Relevant)', kind: 'text' },
    { key: 'brief_summary', label: 'Brief summary', kind: 'markdown' },
  ],
  kickoff_sheet: [{ key: 'kickoff_sheet', label: 'Kickoff Sheet', kind: 'markdown' }],
  client_followup_email: [
    { key: 'subject', label: 'Subject', kind: 'text' },
    { key: 'body', label: 'Body', kind: 'markdown' },
  ],
  internal_notification: [
    { key: 'subject', label: 'Subject', kind: 'text' },
    { key: 'body', label: 'Body', kind: 'markdown' },
  ],
};

/** The editor fields for a type (empty array for an unknown type → free JSON). */
export function editFieldsFor(type: string): readonly DocEditField[] {
  return DOC_EDIT_FORMS[type] ?? [];
}

// ---------------------------------------------------------------------------
// Content-driven edit form — E03-F05-T13
// ---------------------------------------------------------------------------

/**
 * Doc types whose edit form is derived from the ARTIFACT CONTENT via the shared
 * field spec, instead of the hardcoded {@link DOC_EDIT_FORMS} key list.
 *
 * Why this exists: the view was made content-driven in E12-F05-T02 (aliases +
 * an insurance pass over unknown keys) because the generation prompts are
 * client-editable and rename fields between months. The editor was left on its
 * hardcoded list and drifted out of sync, so sections that the view showed came
 * up EMPTY in edit mode:
 *
 *   - `interview_questions` — half the live artifacts write `technical_professional` /
 *     `culture_soft_skills`, which the editor's four literal keys never matched;
 *   - `sourcing_sheet` — worse: the editor's keys are the old *specification*
 *     names (`skillsets_priority_1`, `industry_ideal`, `experience_ideal`, …)
 *     that `SOURCING_SHEET_FIELDS` deliberately dropped because NO live artifact
 *     uses them (see docFields.ts) — so the whole form rendered blank.
 *
 * Driving both from the same spec the view uses is what keeps them from drifting
 * again. `resolveDocFields` returns the key actually found in the content, so
 * read and write both land on the artifact's own naming — no duplicate keys.
 */
const CONTENT_DRIVEN_SPECS: Record<string, readonly DocFieldSpec[]> = {
  interview_questions: INTERVIEW_QUESTION_FIELDS,
  sourcing_sheet: SOURCING_SHEET_FIELDS,
  // The Kickoff Sheet body is markdown stored under `kickoff_sheet` OR `text`
  // (buildKickoffSheet reads both) — the editor knew only the first name, so a
  // `text`-shaped sheet opened blank. Everything else the sheet carries
  // (`header`, `jd_copy`, …) reaches the form through the insurance pass; the
  // nested `sourcing_sheet` object is skipped as non-flat, as it must be.
  kickoff_sheet: [{ key: 'kickoff_sheet', label: 'Kickoff Sheet', aliases: ['text'], block: true }],
};

/**
 * Content keys that must never become an editable field. Deliberately tiny:
 * `status` is the artifact's own lifecycle field, not document content. Anything
 * else non-empty the user can see in the view, the user may edit — that parity
 * IS the fix (E03-F05-T13).
 */
const NEVER_EDITABLE = new Set(['status']);

/**
 * True when a value can round-trip through a text/list field without losing
 * structure. Objects and arrays-of-objects (a nested `sourcing_sheet`, InMail
 * `variants`) are NOT editable as text — flattening them would corrupt the
 * artifact, which is exactly the E03-F05-T12 failure mode.
 */
function isFlatEditableValue(value: unknown): boolean {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return true;
  if (Array.isArray(value))
    return value.every((v) => typeof v === 'string' || typeof v === 'number');
  return false;
}

/** True for a nested content object (`kickoff_sheet.sourcing_sheet`, `…industry`). */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The field kind that edits `value` without reshaping it.
 *
 * Never returns `text`: a `text` field is a bare `<input>` with NO formatting
 * toolbar, and a document body where some sections have the toolbar and others
 * do not is exactly what the human rejected on 2026-09-09 («все поля при
 * редактировании должны быть с одинаковыми инструментами форматирования»).
 * `list`, `richline` and `markdown` all render the same rich-text editor — they
 * differ only in shape (list items) and box height, never in capability.
 */
function kindForValue(value: unknown, block: boolean): DocEditField['kind'] {
  if (Array.isArray(value)) return 'list';
  if (block) return 'markdown';
  if (typeof value === 'string' && (value.includes('\n') || value.length > 200)) return 'markdown';
  return 'richline';
}

/** Read a (possibly nested) `a.b.c` content path. */
export function valueAtPath(content: Record<string, unknown>, path: string): unknown {
  let cur: unknown = content;
  for (const seg of path.split('.')) {
    if (!isPlainObject(cur)) return undefined;
    cur = cur[seg];
  }
  return cur;
}

/**
 * Return a copy of `content` with `path` set to `value`, cloning every object
 * along the way — the artifact is React state, so the nested parents must not be
 * mutated in place.
 */
export function withValueAtPath(
  content: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  if (head === undefined) return content;
  if (rest.length === 0) return { ...content, [head]: value };
  const child = content[head];
  return {
    ...content,
    [head]: withValueAtPath(isPlainObject(child) ? child : {}, rest.join('.'), value),
  };
}

/**
 * The shared field spec to use for a nested object, by its key. A Kickoff Sheet
 * carries a whole Sourcing Sheet under `sourcing_sheet`, and the view renders it
 * with the very same spec (`buildKickoffSheet` → `buildSourcingSheet`), so the
 * editor labels and orders those nested fields identically.
 */
const NESTED_SPECS: Record<string, readonly DocFieldSpec[]> = {
  sourcing_sheet: SOURCING_SHEET_FIELDS,
};

/** Guard against a pathological artifact nesting the editor would drown in. */
const MAX_NESTING = 3;

/**
 * The edit form for a document, derived from its actual content.
 *
 * For a type in {@link CONTENT_DRIVEN_SPECS} the fields come from the shared
 * spec resolved against the content (so aliases and prompt renames are followed).
 * Every other type keeps its hardcoded form. In BOTH cases a final insurance
 * pass appends any remaining non-empty, flat content key the form does not
 * already cover — the same guarantee the view makes: nothing that exists in the
 * artifact is invisible for editing.
 */
export function editFieldsForContent(
  type: string,
  content: Record<string, unknown>,
): readonly DocEditField[] {
  const legacy = editFieldsFor(type);
  const legacyKind = new Map(legacy.map((f) => [f.key, f.kind]));
  const fields: DocEditField[] = [];

  /** Emit the fields of ONE object level; `prefix` is its dot path (''  at top). */
  const emitLevel = (
    obj: Record<string, unknown>,
    prefix: string,
    spec: readonly DocFieldSpec[] | undefined,
    seen: ReadonlySet<string>,
    depth: number,
  ): void => {
    const covered = new Set(seen);

    /** A nested object becomes a group heading plus its own fields. */
    const emitNested = (key: string, label: string, value: Record<string, unknown>): void => {
      if (depth >= MAX_NESTING) return;
      fields.push({ key: `_grp_${prefix}${key}`, label, kind: 'group' });
      emitLevel(value, `${prefix}${key}.`, NESTED_SPECS[key], new Set(), depth + 1);
    };

    if (spec) {
      for (const f of resolveDocFields(spec, obj)) {
        covered.add(f.key);
        // `resolveDocFields` carries its own insurance pass, so unknown keys
        // arrive here too — including ones that are not document content at all.
        if (NEVER_EDITABLE.has(f.key) || isCommentsKey(f.key)) continue;
        if (!f.empty && isPlainObject(f.value)) {
          emitNested(f.key, f.label, f.value);
          continue;
        }
        if (!f.empty && !isFlatEditableValue(f.value)) continue; // e.g. array of objects
        // An empty section still gets a field (you must be able to fill it in);
        // its shape is unknown, so fall back to the legacy kind, then to the
        // spec's own block flag.
        const kind = f.empty
          ? (legacyKind.get(f.key) ?? kindForValue(null, f.block))
          : kindForValue(f.value, f.block);
        fields.push({ key: `${prefix}${f.key}`, label: f.label, kind });
      }
      // `resolveDocFields` marks aliases used internally; the insurance pass
      // below is ours, so record them here too.
      for (const s of spec) {
        covered.add(s.key);
        for (const a of s.aliases ?? []) covered.add(a);
      }
    }

    for (const key of Object.keys(obj).sort()) {
      if (covered.has(key) || NEVER_EDITABLE.has(key) || isCommentsKey(key)) continue;
      const value = obj[key];
      if (isEmptyDocValue(value)) continue;
      if (isPlainObject(value)) {
        emitNested(key, humanFieldLabel(key), value);
        continue;
      }
      if (!isFlatEditableValue(value)) continue;
      fields.push({
        key: `${prefix}${key}`,
        label: humanFieldLabel(key),
        kind: kindForValue(value, false),
      });
    }
  };

  const spec = CONTENT_DRIVEN_SPECS[type];
  const seen = new Set<string>();
  if (!spec) {
    // Types with a working hardcoded form (jd_doc, inmails, the emails) keep it
    // verbatim — including their `variants` and read-only comment blocks.
    for (const f of legacy) {
      fields.push(f);
      seen.add(f.key);
      if (f.kind !== 'comments') seen.add(`${f.key}_comments`);
    }
  }
  emitLevel(content, '', spec, seen, 0);

  return fields;
}

/** True when `type` is a JD-comments field key (read-only grey block). */
export function isCommentsKey(key: string): boolean {
  return key.endsWith('_comments');
}

/** Which actions the preview footer shows for the given status / type. */
export interface PreviewFooterActions {
  readonly showSave: boolean;
  readonly showApprove: boolean;
  readonly showSend: boolean;
}

/** Preview footer rules (T04 acceptance #5): Save in edit mode, Approve, Send. */
export function previewFooterActions(
  status: ArtifactStatus,
  editing: boolean,
  fastTrack: boolean,
): PreviewFooterActions {
  return {
    showSave: editing,
    showApprove: status !== 'approved' && status !== 'sent',
    showSend: fastTrack && (status === 'approved' || status === 'sent'),
  };
}
